/**
 * Deep-link from Rate Lookup (and similar surfaces) into a rate card's line
 * table with PropertyFilter tokens pre-applied.
 */
import {
  KAI_TABLE_FILTERS_EVENT,
  newTokenId,
  serializePropertyFilterQuery,
  type FilterOperator,
  type PropertyFilterQuery,
} from "@/components/table";
import type { RateLineSearchResult } from "@/types/api";

export const RATE_LINES_FILTER_STORAGE_KEY = "twido-v2-rate-lines-filters";

interface RateLineFilterSeed {
  key: string;
  value: string;
  operator?: FilterOperator;
}

function buildQuery(seeds: RateLineFilterSeed[]): PropertyFilterQuery {
  return {
    operation: "and",
    tokens: seeds
      .filter((seed) => seed.value.trim() !== "")
      .map((seed) => ({
        id: newTokenId(),
        propertyKey: seed.key,
        operator: seed.operator ?? ":",
        value: seed.value.trim(),
      })),
  };
}

/** Property-filter query that isolates the clicked search result on its card. */
export function rateLineFiltersFromSearchResult(
  row: RateLineSearchResult,
): PropertyFilterQuery {
  const seeds: RateLineFilterSeed[] = [];

  const origin = row.origin_port?.locode?.trim();
  const dest = row.destination_port?.locode?.trim();
  const size = row.equipment_size?.trim();
  const type = row.equipment_type?.trim();
  const mot = row.pre_carriage_mot?.trim();
  const hasPreCarriage = Boolean(row.pre_carriage_pol) || Boolean(mot);

  if (origin) seeds.push({ key: "origin_port", value: origin });
  if (dest) seeds.push({ key: "destination_port", value: dest });
  if (size) seeds.push({ key: "equipment_size", value: size });
  if (type) seeds.push({ key: "equipment_type", value: type });
  seeds.push({
    key: "has_pre_carriage",
    value: hasPreCarriage ? "true" : "false",
    operator: "=",
  });
  if (hasPreCarriage && mot) {
    seeds.push({ key: "pre_carriage_mot", value: mot, operator: "=" });
  }

  return buildQuery(seeds);
}

/** Lane + equipment seed for isolating a benchmark line on its card. */
export interface RateLineLaneEquipmentSeed {
  originPort?: string | null;
  destinationPort?: string | null;
  equipmentSize?: string | null;
  equipmentType?: string | null;
}

/**
 * Property-filter query that isolates a card's lines to one lane + equipment,
 * e.g. from an OCF reconciliation drill-down so "View published rate" lands on
 * the exact benchmark line.
 */
export function rateLineFiltersFromLaneEquipment(
  seed: RateLineLaneEquipmentSeed,
): PropertyFilterQuery {
  const seeds: RateLineFilterSeed[] = [];
  const origin = seed.originPort?.trim();
  const dest = seed.destinationPort?.trim();
  const size = seed.equipmentSize?.trim();
  const type = seed.equipmentType?.trim();
  if (origin) seeds.push({ key: "origin_port", value: origin });
  if (dest) seeds.push({ key: "destination_port", value: dest });
  if (size) seeds.push({ key: "equipment_size", value: size });
  if (type) seeds.push({ key: "equipment_type", value: type });
  return buildQuery(seeds);
}

/** Write line filters to sessionStorage and notify any mounted RateLinesPanel. */
export function persistRateLineFilters(query: PropertyFilterQuery): void {
  try {
    sessionStorage.setItem(
      RATE_LINES_FILTER_STORAGE_KEY,
      serializePropertyFilterQuery(query),
    );
  } catch {
    // Non-fatal: navigation still lands on the card.
  }

  try {
    window.dispatchEvent(
      new CustomEvent(KAI_TABLE_FILTERS_EVENT, {
        detail: { storageKey: RATE_LINES_FILTER_STORAGE_KEY, query },
      }),
    );
  } catch {
    // Non-fatal.
  }
}

/** Persist row-matching filters and return the rate-card detail path. */
export function buildRateLineDetailUrl(row: RateLineSearchResult): string {
  persistRateLineFilters(rateLineFiltersFromSearchResult(row));
  const base = `/rates/${encodeURIComponent(row.rate_card_id)}`;
  const lineId = row.id?.trim();
  return lineId ? `${base}?line=${encodeURIComponent(lineId)}` : base;
}
